import { type AuthUser } from '@/features/auth/types';
import {
  PROFILE_IMAGE_ACCEPT,
  uploadProfileImage,
} from '@/features/profile/api/directUploads';
import { useProfileImageState } from '@/features/profile/hooks/useProfileImageState';
import { updateCachedUser } from '@/lib/auth';
import {
  buildFieldErrors,
  buildGlobalErrors,
  type FieldErrors,
  mergeFieldErrors,
  type UserError,
} from '@/lib/graphql/errors';
import { useMutation } from '@/lib/graphql/hooks';
import { graphql } from '@/lib/graphql/schema';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import {
  isSupportedLanguage,
  syncDisplayLanguage,
} from '@/lib/i18n/displayLanguage';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const UPDATE_USER_PROFILE_MUTATION = graphql(`
  mutation updateUserProfile($input: UpdateUserProfileInput!) {
    updateUserProfile(input: $input) {
      success
      userErrors {
        message
        code
        path
      }
      user {
        id
        firstName
        lastName
        language
        profileImageUrl
        coverImageUrl
        profileQualityIssues {
          required
          recommended
        }
      }
    }
  }
`);

const FIELD_KEYS = [
  'firstName',
  'lastName',
  'language',
  'profileImageSignedBlobId',
  'coverImageSignedBlobId',
] as const;

export const useUserProfileForm = (user: AuthUser) => {
  const { t } = useTranslation();
  const [updateUserProfile, { data, loading, error }] = useMutation(
    UPDATE_USER_PROFILE_MUTATION
  );
  const [fieldUploadErrors, setFieldUploadErrors] = useState<FieldErrors>({});
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [language, setLanguage] = useState(
    user.language ?? getCurrentLanguage()
  );
  const profileImage = useProfileImageState(user.profileImageUrl ?? null);
  const coverImage = useProfileImageState(user.coverImageUrl ?? null);

  const mutationErrors = data?.updateUserProfile?.userErrors ?? [];
  const fieldErrors = mergeFieldErrors(
    buildFieldErrors(mutationErrors),
    fieldUploadErrors
  );
  const globalErrors = [
    ...buildGlobalErrors(mutationErrors, FIELD_KEYS),
    ...(error
      ? ([
          {
            code: 'NETWORK_ERROR',
            message: t(
              'プロフィールの更新に失敗しました。時間をおいて再度お試しください。'
            ),
          },
        ] satisfies UserError[])
      : []),
  ];

  const hasProfileImageChange =
    Boolean(profileImage.selectedFile) || profileImage.removed;
  const hasCoverImageChange =
    Boolean(coverImage.selectedFile) || coverImage.removed;
  const hasProfileFieldChange =
    firstName.trim() !== (user.firstName ?? '') ||
    lastName.trim() !== (user.lastName ?? '') ||
    language !== (user.language ?? getCurrentLanguage());
  const hasUnsavedChanges =
    hasProfileFieldChange || hasProfileImageChange || hasCoverImageChange;
  const hasOutstandingQualityIssues = Boolean(
    user.profileQualityIssues?.required.length ||
    user.profileQualityIssues?.recommended.length
  );

  const hasValidInput =
    firstName.trim() !== '' && lastName.trim() !== '' && language !== undefined;
  const isSaving = loading || isSubmitting;
  const canSubmit =
    hasValidInput && (hasUnsavedChanges || hasOutstandingQualityIssues);
  const languageChanged = language !== (user.language ?? getCurrentLanguage());

  const saveProfile = async (): Promise<boolean> => {
    if (!canSubmit || isSaving) return false;

    setIsSubmitting(true);
    setFieldUploadErrors({});
    setSavedAt(null);

    try {
      const profileImageSignedBlobId = profileImage.selectedFile
        ? await uploadProfileImage('profile_image', profileImage.selectedFile)
        : undefined;
      const coverImageSignedBlobId = coverImage.selectedFile
        ? await uploadProfileImage('cover_image', coverImage.selectedFile)
        : undefined;

      const result = await updateUserProfile({
        variables: {
          input: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            language,
            profileImageSignedBlobId,
            coverImageSignedBlobId,
            removeProfileImage:
              profileImage.removed && !profileImage.selectedFile
                ? true
                : undefined,
            removeCoverImage:
              coverImage.removed && !coverImage.selectedFile ? true : undefined,
          },
        },
      });

      const response = result.data?.updateUserProfile;
      if (!response?.success || !response.user) return false;

      const updatedUser = response.user;

      syncDisplayLanguage(language);
      updateCachedUser((current) =>
        current
          ? {
              ...current,
              firstName: updatedUser.firstName ?? undefined,
              lastName: updatedUser.lastName ?? undefined,
              language: isSupportedLanguage(updatedUser.language)
                ? updatedUser.language
                : current.language,
              profileImageUrl: updatedUser.profileImageUrl ?? undefined,
              coverImageUrl: updatedUser.coverImageUrl ?? undefined,
              profileQualityIssues:
                (updatedUser.profileQualityIssues as
                  | AuthUser['profileQualityIssues']
                  | undefined) ?? current.profileQualityIssues,
            }
          : current
      );

      profileImage.reset(updatedUser.profileImageUrl ?? null);
      coverImage.reset(updatedUser.coverImageUrl ?? null);
      setSavedAt(new Date().toISOString());
      return true;
    } catch (submitError) {
      console.error('failed to update user profile', submitError);
      setFieldUploadErrors({
        ...(profileImage.selectedFile
          ? {
              profileImageSignedBlobId: [
                t('プロフィール画像のアップロードに失敗しました。'),
              ],
            }
          : {}),
        ...(coverImage.selectedFile
          ? {
              coverImageSignedBlobId: [
                t('カバー画像のアップロードに失敗しました。'),
              ],
            }
          : {}),
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    firstName,
    lastName,
    language,
    profileImage,
    coverImage,
    fieldErrors,
    globalErrors,
    savedAt,
    hasUnsavedChanges,
    canSubmit,
    hasValidInput,
    languageChanged,
    isSaving,
    profileImageAccept: PROFILE_IMAGE_ACCEPT,
    saveProfile,
    setFirstName,
    setLastName,
    setLanguage,
  };
};
